import { ART_PAL, ball, body, fade, mix, n, shade, tint, vgrad, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Deep scene behind the game zone (the SVG fallback until the raster scene is there): the underwater world — light
 * from the surface in the top corners, kelp and corals on the sides, a sandy floor, bubbles, one fish and a small
 * jellyfish at the edges. The centre stays calm (the reels sit there): only water and a soft pool of light. No rays
 * run across the panels. Motifs are marked `data-motif="<x>"` (tests). No filters, no text.
 */
export function deepScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `dp${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;
  const o = (dark: number, light: number) => n(P.dark ? dark : light);

  const top = P.dark ? mix(P.bg, P.sky, 0.24) : mix(P.sky, '#FFFFFF', 0.7);
  const mid = P.dark ? mix(P.bg, P.blue, 0.14) : mix(P.sky, '#FFFFFF', 0.8);
  const low = P.dark ? mix(P.bg, P.blue, 0.06) : mix(P.teal, '#FFFFFF', 0.76);
  const sand = P.dark ? mix(P.bg, P.gold, 0.1) : mix(P.gold, '#FFFFFF', 0.72);
  const kelp = P.dark ? shade(P.teal, 0.5) : mix(P.teal, '#FFFFFF', 0.3);
  const coral = P.dark ? shade(P.pink, 0.2) : tint(P.pink, 0.15);
  const coral2 = P.dark ? shade(P.orange, 0.25) : tint(P.orange, 0.2);
  const fish = P.dark ? shade(P.gold, 0.1) : mix(P.orange, P.gold, 0.5);
  const jelly = P.dark ? P.purple : tint(P.purple, 0.25);
  const ink = P.dark ? '#FFFFFF' : shade(P.sky, 0.3);

  const defs =
    `<defs>${vgrad(`${p}wt`, top, mid, low)}${vgrad(`${p}sd`, sand, mix(sand, low, 0.3), mix(sand, low, 0.5))}` +
    fade(`${p}lt`, P.dark ? tint(P.sky, 0.4) : '#FFFFFF', P.dark ? 0.3 : 0.8) +
    body(`${p}kp`, kelp, 0.3, 0.35, 1, 0.3) +
    body(`${p}co`, coral, 0.35, 0.35) +
    body(`${p}c2`, coral2, 0.35, 0.35) +
    body(`${p}fs`, fish, 0.4, 0.35) +
    ball(`${p}jl`, jelly, 0.4, 0.3) +
    fade(`${p}calm`, P.dark ? P.bg : '#FFFFFF', P.dark ? 0.4 : 0.45) +
    `</defs>`;

  /** a kelp frond: a wavy tapering ribbon from the floor up */
  const frond = (x: number, y: number, h: number, sway: number) =>
    `<path data-motif="${Math.round(x)}" d="M${x - 6} ${y}C${n(x - 6 + sway)} ${n(y - h * 0.3)} ${n(x - 6 - sway)} ${n(y - h * 0.65)} ${n(x + sway * 0.4)} ${n(y - h)}` +
    `C${n(x + 8 - sway)} ${n(y - h * 0.62)} ${n(x + 10 + sway)} ${n(y - h * 0.3)} ${x + 8} ${y}Z" fill="url(#${p}kp)"/>`;
  /** branching coral: a few rounded fingers */
  const coralTree = (x: number, y: number, s: number, g: string) =>
    `<g data-motif="${Math.round(x)}" fill="none" stroke="url(#${g})" stroke-width="${n(12 * s)}" stroke-linecap="round">` +
    `<path d="M${x} ${y}V${n(y - 60 * s)}M${x} ${n(y - 26 * s)}C${n(x - 30 * s)} ${n(y - 34 * s)} ${n(x - 34 * s)} ${n(y - 60 * s)} ${n(x - 34 * s)} ${n(y - 84 * s)}` +
    `M${x} ${n(y - 40 * s)}C${n(x + 26 * s)} ${n(y - 46 * s)} ${n(x + 30 * s)} ${n(y - 70 * s)} ${n(x + 30 * s)} ${n(y - 96 * s)}M${x} ${n(y - 60 * s)}V${n(y - 110 * s)}"/></g>`;
  const bubbles = (x: number, y: number, k: number, r: number) => {
    let s = '';
    for (let i = 0; i < k; i++)
      s += `<circle cx="${n(x + (i % 2 ? 8 : -6) * r * 0.3)}" cy="${n(y - i * r * 3.2)}" r="${n(r * (1 - i * 0.12))}"/>`;
    return `<g fill="none" stroke="${ink}" stroke-opacity="${o(0.22, 0.45)}" stroke-width="1.5">${s}</g>`;
  };
  const fishy = (x: number, y: number, s: number, flip: boolean) =>
    `<g data-motif="${Math.round(x)}" transform="translate(${x} ${y}) scale(${flip ? -s : s} ${s})">` +
    `<path d="M-34 0C-24 -18 10 -20 26 0C10 20 -24 18 -34 0ZM24 0L44 -16V16Z" fill="url(#${p}fs)"/>` +
    `<circle cx="-20" cy="-3" r="3" fill="${shade(fish, 0.7)}"/></g>`;
  const jellyfish = (x: number, y: number, s: number) =>
    `<g data-motif="${Math.round(x)}" fill-opacity="${o(0.7, 0.8)}">` +
    `<path d="M${n(x - 26 * s)} ${y}C${n(x - 26 * s)} ${n(y - 36 * s)} ${n(x + 26 * s)} ${n(y - 36 * s)} ${n(x + 26 * s)} ${y}Z" fill="url(#${p}jl)"/>` +
    `<path d="M${n(x - 14 * s)} ${y}c-4 ${n(16 * s)} 4 ${n(28 * s)} 0 ${n(44 * s)}M${x} ${y}c4 ${n(16 * s)} -4 ${n(30 * s)} 0 ${n(50 * s)}M${n(x + 14 * s)} ${y}c-4 ${n(16 * s)} 4 ${n(26 * s)} 0 ${n(40 * s)}" fill="none" stroke="${jelly}" stroke-opacity=".6" stroke-width="2"/></g>`;

  const floor = wide ? 780 : 700;
  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}wt)"/>` +
    // light from the surface: two soft pools in the top corners (no rays across the panels)
    `<ellipse cx="${n(W * 0.12)}" cy="-20" rx="${n(W * 0.24)}" ry="${n(H * 0.3)}" fill="url(#${p}lt)"/>` +
    `<ellipse cx="${n(W * 0.88)}" cy="-20" rx="${n(W * 0.24)}" ry="${n(H * 0.3)}" fill="url(#${p}lt)"/>` +
    // sandy floor, a gentle dune
    `<path d="M0 ${floor}C${W * 0.2} ${floor - 30} ${W * 0.4} ${floor + 10} ${W * 0.6} ${floor - 6}S${W * 0.88} ${floor - 30} ${W} ${floor - 10}V${H}H0Z" fill="url(#${p}sd)"/>` +
    `<ellipse cx="${W / 2}" cy="${H * 0.5}" rx="${n(W * 0.38)}" ry="${n(H * 0.42)}" fill="url(#${p}calm)"/>`;

  if (wide) {
    art +=
      frond(230, floor + 20, 420, 26) +
      frond(270, floor + 30, 300, -20) +
      frond(1360, floor + 10, 380, -24) +
      frond(1400, floor + 20, 260, 18) +
      coralTree(330, floor + 30, 1.2, `${p}co`) +
      coralTree(1270, floor + 20, 1, `${p}c2`) +
      coralTree(1450, floor + 40, 0.9, `${p}co`) +
      bubbles(360, 520, 5, 7) +
      bubbles(1250, 420, 4, 6) +
      fishy(1330, 300, 1, true) +
      fishy(1250, 350, 0.6, true) +
      jellyfish(300, 250, 1);
  } else {
    art +=
      frond(30, floor + 40, 300, 18) +
      frond(372, floor + 40, 240, -16) +
      coralTree(330, 590, 0.7, `${p}co`) +
      bubbles(70, 540, 4, 5) +
      fishy(330, 110, 0.6, true) +
      jellyfish(56, 100, 0.6);
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">` +
    defs +
    art +
    `</svg>`
  );
}

/** Header accent of Deep: a gentle wave with rising bubbles (repeat-x tile, 48 × 12). */
export function deepAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const wave = P.dark ? P.sky : shade(P.sky, 0.05);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 12" width="48" height="12">` +
    `<path d="M0 9Q12 5 24 9T48 9" fill="none" stroke="${wave}" stroke-opacity=".7" stroke-width="1.4"/>` +
    `<circle cx="30" cy="4" r="1.6" fill="none" stroke="${wave}" stroke-opacity=".6"/>` +
    `<circle cx="34" cy="1.8" r="1" fill="none" stroke="${wave}" stroke-opacity=".5"/>` +
    `</svg>`
  );
}

export default defineScene({ game: 'deep', scene: deepScene, accent: deepAccent });
